import { botFightMode } from './entries/bot.js';
import {
  managedCloudflare,
  managedCloudflareFree,
  managedOwasp,
} from './entries/managed-rulesets.js';
import {
  alwaysUseHttps,
  automaticHttpsRewrites,
  brotli,
  browserCheck,
  challengeTtl,
  minTlsVersion,
  securityLevel,
  ssl,
} from './entries/zone-settings.js';
import { isResolved } from './scopes.js';
import type { CatalogKind, ValueNote } from './types.js';
import { allowedValues, isAllowedValue, type InferValue } from './value-spec.js';
import type { Plan } from './constants.js';

export * from './constants.js';
export * from './scopes.js';
export * from './types.js';
export * from './value-spec.js';

/**
 * JEDNO ŹRÓDŁO PRAWDY.
 *
 * Z tego obiektu wyprowadzamy: formularz pracownika, schemat Zod walidujący
 * preset na backendzie oraz opisy prostym językiem na ekranie klienta.
 * Lista nie jest duplikowana nigdzie indziej.
 */
export const CATALOG = {
  ssl,
  always_use_https: alwaysUseHttps,
  automatic_https_rewrites: automaticHttpsRewrites,
  min_tls_version: minTlsVersion,
  security_level: securityLevel,
  browser_check: browserCheck,
  challenge_ttl: challengeTtl,
  brotli,
  bot_fight_mode: botFightMode,
  managed_cloudflare_free: managedCloudflareFree,
  managed_cloudflare: managedCloudflare,
  managed_owasp: managedOwasp,
} as const;

export type CatalogKey = keyof typeof CATALOG;
export type CatalogEntryFor<K extends CatalogKey> = (typeof CATALOG)[K];
export type CatalogValue<K extends CatalogKey> = InferValue<CatalogEntryFor<K>['value']>;

export const CATALOG_KEYS = Object.keys(CATALOG) as readonly CatalogKey[];

export function isCatalogKey(key: string): key is CatalogKey {
  return Object.prototype.hasOwnProperty.call(CATALOG, key);
}

/** Wewnętrzny alias — `changePl` jest typowany per wpis, iterujemy generycznie. */
type ChangeFn = (value: unknown) => string;

/** Zdanie prostym językiem opisujące zmianę tego ustawienia na daną wartość. */
export function describeChange<K extends CatalogKey>(key: K, value: CatalogValue<K>): string {
  return (CATALOG[key].customer.changePl as ChangeFn)(value);
}

/** Uwaga przypięta do konkretnej wartości, jeśli istnieje. */
export function valueNote(key: CatalogKey, value: unknown): ValueNote | undefined {
  return CATALOG[key].valueNotes?.[String(value)];
}

/** Wartości, których nie wolno zapisać w presecie (np. `ssl: flexible`). */
export function isBlockedValue(key: CatalogKey, value: unknown): boolean {
  return valueNote(key, value)?.severity === 'blocking';
}

/** Wszystkie pary (klucz, wartość) oznaczone jako blokujące — do testów i UI. */
export function blockedValues(): readonly { key: CatalogKey; value: string }[] {
  const out: { key: CatalogKey; value: string }[] = [];
  for (const key of CATALOG_KEYS) {
    const notes = CATALOG[key].valueNotes;
    if (!notes) continue;
    for (const [value, note] of Object.entries(notes)) {
      if (note.severity === 'blocking') out.push({ key, value });
    }
  }
  return out;
}

export function isAllowedCatalogValue(key: CatalogKey, value: unknown): boolean {
  return isAllowedValue(CATALOG[key].value, value);
}

/** Scope'y OAuth wymagane przez zbiór ustawień. Bez duplikatów. */
export function requiredScopes(keys: readonly CatalogKey[]): readonly string[] {
  const set = new Set<string>();
  for (const key of keys) for (const s of CATALOG[key].scopes) set.add(s);
  return [...set];
}

// --- Serializacja dla frontu ---------------------------------------------
// `changePl` jest funkcją, więc katalogu nie da się wprost wysłać jako JSON.
// Zamiast tego liczymy zdanie po polsku dla KAŻDEJ dozwolonej wartości —
// front dostaje gotowe teksty i nie musi znać logiki.

export type SerializedCatalogValue = {
  readonly value: string | number | boolean;
  readonly changePl: string;
  readonly note?: ValueNote;
};

export type SerializedCatalogEntry = {
  readonly key: CatalogKey;
  readonly kind: CatalogKind;
  readonly label: string;
  readonly hint: string;
  readonly plans: { readonly min: Plan; readonly max?: Plan };
  readonly scopes: readonly string[];
  readonly scopesResolved: boolean;
  readonly deprecated?: { readonly sincePl: string };
  readonly dependsOn?: readonly string[];
  readonly recommended?: string | number | boolean;
  /** `settingId` dla zone-setting, `field` dla bot-config, `rulesetId` dla managed-ruleset. */
  readonly target: string;
  readonly phase?: string;
  readonly values: readonly SerializedCatalogValue[];
};

function targetOf(key: CatalogKey): { target: string; phase?: string } {
  const entry = CATALOG[key];
  switch (entry.kind) {
    case 'zone-setting':
      return { target: entry.settingId };
    case 'bot-config':
      return { target: entry.field };
    case 'managed-ruleset':
      return { target: entry.rulesetId, phase: entry.phase };
  }
}

export function serializeCatalogEntry(key: CatalogKey): SerializedCatalogEntry {
  const entry = CATALOG[key];
  const changePl = entry.customer.changePl as ChangeFn;

  const values: SerializedCatalogValue[] = allowedValues(entry.value).map((value) => {
    const note = valueNote(key, value);
    return note ? { value, changePl: changePl(value), note } : { value, changePl: changePl(value) };
  });

  const { target, phase } = targetOf(key);

  return {
    key,
    kind: entry.kind,
    label: entry.staff.label,
    hint: entry.staff.hint,
    plans: entry.plans,
    scopes: [...entry.scopes],
    scopesResolved: entry.scopes.every(isResolved),
    ...(entry.deprecated ? { deprecated: entry.deprecated } : {}),
    ...(entry.dependsOn ? { dependsOn: entry.dependsOn } : {}),
    ...(entry.recommended !== undefined
      ? { recommended: entry.recommended as string | number | boolean }
      : {}),
    target,
    ...(phase ? { phase } : {}),
    values,
  };
}

export function serializeCatalog(): readonly SerializedCatalogEntry[] {
  return CATALOG_KEYS.map(serializeCatalogEntry);
}
